import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

type InlineEditableFieldProps = {
  label?: string;
  value?: string | number | null;
  displayValue?: string | null;
  placeholder?: string;
  type?: string;
  forceEdit?: boolean;
  disabled?: boolean;
  className?: string;
  onSave: (newValue: string) => Promise<void> | void;
  // When bulkEdit is true, render as a plain input without action icons
  // and call onChange for draft updates instead of saving immediately.
  bulkEdit?: boolean;
  onChange?: (newValue: string) => void;
};

export const InlineEditableField: React.FC<InlineEditableFieldProps> = ({
  label,
  value,
  displayValue,
  placeholder,
  type = 'text',
  forceEdit = false,
  disabled = false,
  className,
  onSave,
  bulkEdit = false,
  onChange,
}) => {
  const [editing, setEditing] = useState<boolean>(!!forceEdit);
  const [current, setCurrent] = useState<string>(value == null ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setEditing(!!forceEdit);
  }, [forceEdit]);

  useEffect(() => {
    if (!editing) {
      setCurrent(value == null ? '' : String(value));
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (disabled) return;
    try {
      setSaving(true);
      await onSave(current);
      setEditing(!!forceEdit); // if forceEdit, remain editing; otherwise exit edit mode
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCurrent(value == null ? '' : String(value));
    setEditing(!!forceEdit);
  };

  if (bulkEdit) {
    return (
      <div className={className}>
        {label && (
          <dt className="text-[12px] font-medium text-[rgb(120,113,108)]">{label}</dt>
        )}
        <Input
          ref={inputRef}
          type={type}
          value={current}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            setCurrent(e.target.value);
            onChange?.(e.target.value);
          }}
          className="h-8"
        />
      </div>
    );
  }

  if (!editing) {
    return (
      <div className={className}>
        {label && (
          <dt className="text-[12px] font-medium text-[rgb(120,113,108)]">{label}</dt>
        )}
        <dd className="cursor-pointer" onClick={() => !disabled && setEditing(true)}>
          {displayValue != null && String(displayValue).trim() !== ''
            ? String(displayValue)
            : value && String(value).trim() !== ''
            ? String(value)
            : '—'}
        </dd>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <dt className="text-[12px] font-medium text-[rgb(120,113,108)]">{label}</dt>
      )}
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          type={type}
          value={current}
          placeholder={placeholder}
          disabled={disabled || saving}
          onChange={(e) => setCurrent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          className="h-8"
        />
        <Button size="icon" variant="ghost" disabled={saving} onClick={handleSave} title="Save">
          <Check className="h-4 w-4 text-emerald-600" />
        </Button>
        <Button size="icon" variant="ghost" disabled={saving} onClick={handleCancel} title="Cancel">
          <X className="h-4 w-4 text-gray-500" />
        </Button>
      </div>
    </div>
  );
};

export default InlineEditableField;
